import { useEffect, useMemo, useState } from 'react';

import { IconButton } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { Progress } from './VoiceEditor.js';
import { formatBytes, useSoundCache, type CacheEntry } from './sound-api.js';
import './sound-editors.css';

function EntryRow({ e }: { e: CacheEntry }) {
  const dl = useSoundCache((s) => s.downloads[`${e.kind}:${e.id}`]);
  const download = useSoundCache((s) => s.download);
  return (
    <li className={`snd-dl${e.installed ? ' is-installed' : ''}`}>
      <Icon name={e.kind === 'soundfont' ? 'audio' : 'sparkles'} size={14} />
      <div className="snd-dl-text">
        <span className="snd-dl-name">
          {e.name}
          {e.isDefault && <span className="snd-chip">default</span>}
          {e.quality && e.kind === 'voice' && <span className="snd-chip">{e.quality}</span>}
          {e.speakers && e.speakers > 1 ? <span className="snd-chip">{`${e.speakers} speakers`}</span> : null}
        </span>
        <span className="snd-dl-sub">
          {[e.name !== e.id ? e.id : null, formatBytes(e.size), e.license, e.attribution].filter(Boolean).join(' · ')}
        </span>
        {dl?.error && <span className="snd-hint is-error">{dl.error}</span>}
      </div>
      {e.installed ? (
        <span className="snd-chip is-ok">
          <Icon name="check" size={11} /> installed
        </span>
      ) : dl && !dl.error ? (
        <Progress value={dl.progress} label="Downloading…" />
      ) : (
        <button type="button" className="snd-btn" onClick={() => void download(e.kind, e.id)}>
          {dl?.error ? 'Retry' : `Download${e.size ? ` ${formatBytes(e.size)}` : ''}`}
        </button>
      )}
    </li>
  );
}

/**
 * The shared download cache (all projects): General MIDI SoundFonts and
 * Piper TTS voices, installed and available, with sizes, licenses and
 * downloads with progress (`POST /api/sound/cache/download`).
 */
export function DownloadsPanel() {
  const entries = useSoundCache((s) => s.entries);
  const loading = useSoundCache((s) => s.loading);
  const error = useSoundCache((s) => s.error);
  const load = useSoundCache((s) => s.load);
  const [query, setQuery] = useState('');
  const [lang, setLang] = useState('');
  const [installedOnly, setInstalledOnly] = useState(false);
  useEffect(() => void load(), [load]);

  const fonts = entries.filter((e) => e.kind === 'soundfont');
  const voices = entries.filter((e) => e.kind === 'voice');
  const langs = useMemo(() => [...new Set(voices.map((v) => v.language).filter((l): l is string => !!l))].sort(), [voices]);
  const needle = query.trim().toLowerCase();
  const shownVoices = voices
    .filter((v) => (!lang || v.language === lang) && (!installedOnly || v.installed) && (!needle || `${v.id} ${v.name} ${v.description ?? ''}`.toLowerCase().includes(needle)))
    .sort((a, b) => Number(b.installed) - Number(a.installed) || a.id.localeCompare(b.id));
  const installedBytes = entries.filter((e) => e.installed).reduce((s, e) => s + e.size, 0);

  return (
    <div className="snd-downloads">
      <div className="snd-row">
        <span className="snd-hint">{`Shared cache for all projects · ${entries.filter((e) => e.installed).length} installed (${formatBytes(installedBytes)})`}</span>
        <span className="snd-spacer" />
        <IconButton icon="refresh" title="Refresh" size={13} onClick={() => void load(true)} />
      </div>
      {error && <span className="snd-hint is-error">{error}</span>}
      {loading && entries.length === 0 && <span className="snd-hint">Loading…</span>}

      <h4 className="snd-h">SoundFonts</h4>
      {fonts.length === 0 && !loading && <span className="snd-hint">No SoundFont listed.</span>}
      <ul className="snd-dl-list">
        {fonts.map((e) => (
          <EntryRow key={e.id} e={e} />
        ))}
      </ul>

      <h4 className="snd-h">{`Voices (${voices.filter((v) => v.installed).length}/${voices.length} installed)`}</h4>
      <div className="snd-row">
        <div className="snd-search">
          <Icon name="search" size={13} />
          <input value={query} placeholder="Search voices…" onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="selectfield">
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="">All languages</option>
            {langs.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <Icon name="chevronDown" size={12} />
        </div>
        <label className="snd-check">
          <input type="checkbox" checked={installedOnly} onChange={(e) => setInstalledOnly(e.target.checked)} />
          installed
        </label>
      </div>
      <ul className="snd-dl-list">
        {shownVoices.slice(0, 300).map((e) => (
          <EntryRow key={e.id} e={e} />
        ))}
      </ul>
      {shownVoices.length > 300 && <span className="snd-hint">{`${shownVoices.length - 300} more — refine the search.`}</span>}
      {voices.length > 0 && shownVoices.length === 0 && <span className="snd-hint">No voice matches.</span>}
    </div>
  );
}
